import React from 'react';
import { useParams, useLocation } from 'react-router-dom';
import Hero from '../components/Hero';
import Summary from '../components/Summary';
import CardList from '../components/CardList';
import ContentSection from '../components/ContentSection';
import ServiceList from '../components/ServiceList';
import FrequentAsked from '../components/FrequentAsked';
import SEO from '../components/SEO';
import './Content.css';
import resolveImageUrl from '../utils/imageResolver';

/**
 * Generic content renderer for immigration-like pages.
 * Props:
 * - data: Record<string, PageData>
 * - rootClassName: string (root container class)
 * - sectionPrefix: string (prefix for per-section class names)
 * - notFoundText?: string
 */
const Content = ({ page: pageProp, data, rootClassName, sectionPrefix, notFoundText = 'Page not found' }) => {
  const { type } = useParams();
  const location = useLocation();
  const page = pageProp || (data ? (type ? data[type] : null) : null);

  if (!page) {
    return <div className="text-center mt-5">{notFoundText}</div>;
  }

  // Generate SEO data from page content
  const pageTitle = page.hero?.title || '留洋教育科技公司';
  const pageDescription = page.hero?.subtitle || page.summary?.content?.substring(0, 160) || '專業留學與移民服務';
  const canonicalUrl = `https://liuyanghorizon.com${location.pathname}`;
  const ogImage = page.hero?.imageUrl || 'https://liuyanghorizon.com/images/logo.png';

  const mapCardBg = (bg) => (bg === 'white' ? 'white' : 'gray');

  return (
    <div className={rootClassName || 'generic-page'}>
      <SEO
        title={`${pageTitle} - 留洋教育科技公司`}
        description={pageDescription}
        keywords="留學申請,移民服務,香港留學,美國留學,新加坡留學,留學規劃"
        canonical={canonicalUrl}
        ogImage={ogImage}
      />
      {page.hero && (
        <Hero
          title={page.hero.title || ''}
          subtitle={page.hero.subtitle || ''}
          imageUrl={resolveImageUrl(page.hero.imageUrl, {
            width: 1200,
            height: 400,
            text: page.hero.title,
            keywords: [page.hero.subtitle]
          })}
        />
      )}

      {page.summary && (page.summary.imageUrl || page.summary.content || page.summary.sideText) && (
        <Summary
          title={page.summary.title}
          content={page.summary.content}
          imageUrl={page.summary.imageUrl}
          imageAlt={page.summary.imageAlt}
          sideText={page.summary.sideText}
          className="study-summary-section"
        />
      )}

      {Array.isArray(page.sections) &&
        page.sections.map((section, idx) => {
          if (section.type === 'cardList') {
            return (
              <section key={idx} className={`${sectionPrefix || 'section'}-${idx}`}>
                <CardList
                  title={section.title}
                  titleDescription={section.titleDescription || ''}
                  cardListData={section.data || []}
                  body={section.body || ''}
                  backgroundColor={mapCardBg(section.backgroundColor)}
                />
              </section>
            );
          }

          if (section.type === 'faqs') {
            return (
              <section key={idx} className={`${sectionPrefix || 'section'}-${idx}`}>
                <div className="container">
                  <FrequentAsked title={section.title} questions={section.questions || []} />
                </div>
              </section>
            );
          }

          if (section.type === 'contentSection') {
            return (
              <section key={idx} className={`${sectionPrefix || 'section'}-${idx}`}>
                <ContentSection
                  title={section.title}
                  subtitle={section.subtitle}
                  contentText={section.contentText}
                  contentList={section.contentList}
                  contentDisplaySide={section.contentDisplaySide || 'left'}
                  backgroundImageUrl={section.backgroundImageUrl}
                />
              </section>
            );
          }

          if (section.type === 'serviceList') {
            return (
              <section key={idx} className={`${sectionPrefix || 'section'}-${idx}`}>
                <ServiceList
                  title={section.title}
                  titleDescription={section.titleDescription || ''}
                  services={section.services || []}
                  backgroundColor={mapCardBg(section.backgroundColor)}
                  body={section.body || ''}
                />
              </section>
            );
          }

          return null;
        })}
    </div>
  );
};

export default Content;
